import React, { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext({ lang: "en", setLang: () => {}, t: (v) => v });

const LANG_KEY = "cuquito_lang";

// Pick right string from bilingual object {en, es}, or return string as-is
export function pick(v, lang) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    return v[lang] || v.en || v.es || "";
  }
  return String(v);
}

// UI strings (not in DB, hardcoded in both languages)
export const UI = {
  scroll: { en: "SCROLL", es: "DESLIZA" },
  bilingual: { en: "[ Bilingual crew · EN / ES ]", es: "[ Equipo bilingüe · ES / EN ]" },
  wa: { en: "WhatsApp", es: "WhatsApp" },
  menuNav: { en: "Menu", es: "Menú" },
  navServices: { en: "Services", es: "Servicios" },
  navMethod: { en: "Method", es: "Método" },
  navWork: { en: "Work", es: "Trabajos" },
  navReviews: { en: "Reviews", es: "Reseñas" },
  navContact: { en: "Contact", es: "Contacto" },
  // section eyebrows
  section02: { en: "/ 02 — Services", es: "/ 02 — Servicios" },
  section03: { en: "/ 03 — Method", es: "/ 03 — Método" },
  section04: { en: "/ 04 — Portfolio", es: "/ 04 — Portfolio" },
  section05: { en: "/ 05 — Voices", es: "/ 05 — Voces" },
  section06: { en: "/ 06 — Contact", es: "/ 06 — Contacto" },
  // services headline
  servicesH1: { en: "What we do", es: "Lo que hacemos" },
  servicesH2: { en: "better than anyone.", es: "mejor que nadie." },
  // gallery
  galleryH1: { en: "Recent", es: "Trabajos" },
  galleryH2: { en: "work.", es: "recientes." },
  galleryCta: { en: "Quote yours", es: "Cotiza el tuyo" },
  // testimonials
  testH1: { en: "Our clients", es: "Nuestros clientes" },
  testH2: { en: "speak for us.", es: "hablan por nosotros." },
  // contact
  contactH1: { en: "Get your", es: "Pide tu" },
  contactH2: { en: "quote.", es: "cotización." },
  contactSub: {
    en: "We reply within 24 hours. Free estimate, no strings attached, with clear pricing.",
    es: "Respondemos en menos de 24 horas. Cotización sin costo, sin compromiso y con precio claro.",
  },
  contactPhoneLabel: { en: "Direct phone", es: "Teléfono directo" },
  contactHours: { en: "Mon — Sat · 7 a.m. — 8 p.m.", es: "Lun — Sáb · 7 a.m. — 8 p.m." },
  contactWaLabel: { en: "WhatsApp 24/7", es: "WhatsApp 24/7" },
  contactWaHint: { en: "Send a photo of the project", es: "Envía foto del proyecto" },
  contactAreaLabel: { en: "Service area", es: "Área de servicio" },
  formTag: { en: "[ FORM · 01 ] Quote request", es: "[ FORM · 01 ] Solicitud de cotización" },
  formName: { en: "Name*", es: "Nombre*" },
  formPhone: { en: "Phone*", es: "Teléfono*" },
  formEmail: { en: "Email (optional)", es: "Correo (opcional)" },
  formService: { en: "Service", es: "Servicio" },
  formMessage: { en: "Tell us about the project", es: "Cuéntanos del proyecto" },
  formDisclaimer: {
    en: "By submitting, you agree to be contacted by phone, WhatsApp or email.",
    es: "Al enviar aceptas ser contactado por teléfono, WhatsApp o correo.",
  },
  formSubmit: { en: "Send request", es: "Enviar solicitud" },
  formSending: { en: "Sending...", es: "Enviando..." },
  formSuccess: { en: "Thanks! We'll reach out in less than 24h.", es: "¡Gracias! Te contactamos en menos de 24h." },
  formError: { en: "Couldn't send. Call us at ", es: "No se pudo enviar. Llámanos al " },
  formRequired: { en: "Name and phone are required", es: "Nombre y teléfono son obligatorios" },
  // footer
  footerContact: { en: "Contact", es: "Contacto" },
  footerNav: { en: "Navigation", es: "Navegación" },
  footerRights: { en: "All rights reserved", es: "Todos los derechos reservados" },
  footerSince: { en: "Since", es: "Desde" },
  // hero bar
  filePrefix: { en: "EST ·", es: "FUND ·" },
  fileSuffix: { en: "FILE / 001", es: "FILE / 001" },
  // loader
  loading: { en: "LOADING", es: "CARGANDO" },
};

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(LANG_KEY) || "en";
    } catch {
      return "en";
    }
  });
  const setLang = (l) => {
    setLangState(l);
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch (_e) {
      /* ignore quota errors */
    }
    if (typeof document !== "undefined") document.documentElement.lang = l;
  };
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const t = (v) => pick(v, lang);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}
